import {
  ToitEnPenteInsulationTypes,
  ToitEnPenteClimateTypes,
  ToitEnPenteStructureTypes,
  ToitEnPenteRainCoverTypes,
  ToitEnPenteAcousticLayerTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { ToitEnPenteData } from "../toit-en-pente/store"
import { useToitEnPente } from "../toit-en-pente/hooks"
import { checkValidQueriedValueOfType, getEnumKey } from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setToitInsulationType,
    setToitClimateType,
    setToitStructureType,
    setToitRainCoverType,
    setToitAcousticLayerType,
    setToitInsulationTypeWithThickness,
  } = useToitEnPente()

  const setQueryParams = (
    data: ToitEnPenteData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.TOIT_EN_PENTE_3_OVERVIEW", {
      lng: language,
    })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Toit en pente insulation
    const toitEnPenteInsulationType = data.toitInsulation.type
    const toitEnPenteInsulationTypeEnum = getEnumKey(
      toitEnPenteInsulationType,
      ToitEnPenteInsulationTypes
    )
    if (toitEnPenteInsulationTypeEnum) {
      url.searchParams.append("insulation", toitEnPenteInsulationTypeEnum)

      // Include toit insulation thickness
      const toitEnPenteInsulationThermalThickness =
        data.thermalPerformance.toitInsulation.thickness
      if (toitEnPenteInsulationThermalThickness) {
        url.searchParams.append(
          "insulation-thickness",
          toitEnPenteInsulationThermalThickness.toString()
        )
      }
    }

    // Toit en pente climate
    const toitEnPenteClimateType = data.toitClimate.type
    const toitEnPenteClimateTypeEnum = getEnumKey(
      toitEnPenteClimateType,
      ToitEnPenteClimateTypes
    )
    if (toitEnPenteClimateTypeEnum) {
      url.searchParams.append("climate", toitEnPenteClimateTypeEnum)
    }

    // Toit en pente structure
    const toitEnPenteStructureType = data.toitStructure.type
    const toitEnPenteStructureTypeEnum = getEnumKey(
      toitEnPenteStructureType,
      ToitEnPenteStructureTypes
    )
    if (toitEnPenteStructureTypeEnum) {
      url.searchParams.append("structure", toitEnPenteStructureTypeEnum)
    }

    // Toit en pente rain cover
    const toitEnPenteRainCoverType = data.toitRainCover.type
    const toitEnPenteRainCoverTypeEnum = getEnumKey(
      toitEnPenteRainCoverType,
      ToitEnPenteRainCoverTypes
    )
    if (toitEnPenteRainCoverTypeEnum) {
      url.searchParams.append("rain-cover", toitEnPenteRainCoverTypeEnum)
    }

    // Toit en pente acoustic layer
    const toitEnPenteAcousticLayerType = data.toitAcousticLayer.type
    const toitEnPenteAcousticLayerTypeEnum = getEnumKey(
      toitEnPenteAcousticLayerType,
      ToitEnPenteAcousticLayerTypes
    )
    if (toitEnPenteAcousticLayerTypeEnum) {
      url.searchParams.append(
        "acoustic-layer",
        toitEnPenteAcousticLayerTypeEnum
      )
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      insulation: url.searchParams.get("insulation"),
      insulationThickness: url.searchParams.get("insulation-thickness"),
      climate: url.searchParams.get("climate"),
      structure: url.searchParams.get("structure"),
      rainCover: url.searchParams.get("rain-cover"),
      acousticLayer: url.searchParams.get("acoustic-layer"),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Toit en pente insulation
    const validInsulation = checkValidQueriedValueOfType(
      query.insulation,
      ToitEnPenteInsulationTypes
    )
    if (validInsulation) {
      // Include toit insulation thickness
      if (query.insulationThickness) {
        setToitInsulationTypeWithThickness(
          validInsulation,
          query.insulationThickness
        )
      } else {
        setToitInsulationType(validInsulation)
      }
    }

    // Toit en pente climate
    const validClimate = checkValidQueriedValueOfType(
      query.climate,
      ToitEnPenteClimateTypes
    )
    if (validClimate) setToitClimateType(validClimate)

    // Toit en pente structure
    const validStructure = checkValidQueriedValueOfType(
      query.structure,
      ToitEnPenteStructureTypes
    )
    if (validStructure) setToitStructureType(validStructure)

    // Toit en pente rain cover
    const validRainCover = checkValidQueriedValueOfType(
      query.rainCover,
      ToitEnPenteRainCoverTypes
    )
    if (validRainCover) setToitRainCoverType(validRainCover)

    // Toit en pente acoustic layer
    const validAcousticLayer = checkValidQueriedValueOfType(
      query.acousticLayer,
      ToitEnPenteAcousticLayerTypes
    )
    if (validAcousticLayer) setToitAcousticLayerType(validAcousticLayer)

    // Model
    const validModel = checkValidQueriedValueOfType(query.detail, Models)
    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.TOIT_EN_PENTE)

    // Routing
    let route = Routes.TOIT_EN_PENTE_1_STRUCTURE
    if (
      validInsulation &&
      validClimate &&
      validStructure &&
      validRainCover &&
      validAcousticLayer
    ) {
      route = Routes.TOIT_EN_PENTE_3_OVERVIEW
    } else if (
      validInsulation &&
      validClimate &&
      validStructure &&
      validRainCover
    ) {
      route = Routes.TOIT_EN_PENTE_2_RAIN_COVER
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
